import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useMatch, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { PUBLIC_NAV_ITEMS, filterVisibleItems } from '../config/navigationConfig';
import OroLockup from './OroLockup';

/**
 * PublicHeader Component
 * 
 * Reusable header for public pages using shared navigation config.
 * Uses md breakpoint for consistency with authenticated app navigation.
 * 
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

interface PublicHeaderProps {
  showSignIn?: boolean;
}

export default function PublicHeader({ showSignIn = true }: PublicHeaderProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openLoginModal } = useAuthModal();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // #3126 r1 (George M2): the "Get the iPhone app" affordance is a dead control
  // on /app itself (that page IS the app landing surface), so hide the text link
  // there. The App Store badge in the page close stays the primary action.
  // #3126 r2 (Frank Low): useMatch('/app') so the trailing-slash variant `/app/`
  // (served by the SPA catch-all) also suppresses the self-link — an exact
  // `pathname === '/app'` compare would leak it back on `/app/`.
  const onAppPage = useMatch('/app') !== null;
  const visibleNavItems = filterVisibleItems(PUBLIC_NAV_ITEMS);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleSignIn = () => {
    closeMenu();
    
    // If already authenticated, redirect to app home
    if (isAuthenticated) {
      navigate('/home');
    } else {
      openLoginModal();
    }
  };

  return (
    <>
      {/* #3840: z-[30] is Z_LAYERS.topBar, written as a literal so Tailwind
          emits it. See the mobile menu comment below. */}
      <header className="px-4 pt-6 pb-4 relative z-[30] bg-oro-surface/95 backdrop-blur-sm border-b border-oro-border">
        <div className="flex items-center justify-between">
          {/* #2544: the official horizontal lockup, not a hand-composed
              mark + text wordmark. Same brand-owner call as the web login
              modal (#2523) and the native login screen (#2516).

              `size` is the WIDTH here — the horizontal asset is trimmed to its
              ink, so height follows the 1200x289 aspect (~36px at 150).

              NOT `decorative`: with the text span gone, the lockup's
              alt="ORO move" is this button's only accessible name. */}
          <button
            onClick={() => navigate('/')}
            className="hover:opacity-80 transition-opacity"
          >
            <OroLockup orientation="horizontal" size={150} />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {item.label}
              </Link>
            ))}
            {/* #3126: the "Get the iPhone app" affordance now points at the
                /app landing page (a real client route), so it is a react-router
                <Link>. The store handoff (/ios, the _redirects 302) stays on
                the App Store badge and the FAQ. ~90% of the audience is on iOS
                and the site otherwise only ever says "sign in". Hidden on /app
                itself (George M2), where it would be a self-link. */}
            {!onAppPage && (
              <Link
                to="/app"
                className="inline-flex items-center min-h-11 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Get the iPhone app
              </Link>
            )}
            {showSignIn && (
              <button
                onClick={handleSignIn}
                className="text-sm text-accent hover:text-accent-light transition-colors font-medium ml-4"
              >
                Sign in
              </button>
            )}
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="p-2 text-text-primary hover:text-accent transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - rendered via portal to escape stacking context issues (#1331).
          #3840: z-[50] is Z_LAYERS.overlay, written as a literal because Tailwind
          scans source for class strings. getZIndexClass('overlay') computes the
          class at runtime, so no z-index rule ships, the panel stays z-index:auto,
          and the hero's z-10 content paints over it (same trap as #3324). */}
      {isMenuOpen && createPortal(
        <div className="fixed top-16 right-4 w-64 z-[50] bg-oro-surface border border-oro-border rounded-lg shadow-lg md:hidden">
          <nav className="py-2" role="menu">
            {visibleNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMenu}
                className="block px-4 py-3 text-text-primary hover:bg-oro-surface-alt transition-colors"
                role="menuitem"
              >
                {item.label}
              </Link>
            ))}
            {/* #3126: the mobile-menu "Get the iPhone app" affordance points at
                the /app landing page (a react-router <Link>), closing the menu
                on tap. The store 302 (/ios) stays on the badge + FAQ. Hidden on
                /app itself (George M2), where it would be a self-link. */}
            {!onAppPage && (
              <Link
                to="/app"
                onClick={closeMenu}
                className="block px-4 py-3 text-text-primary hover:bg-oro-surface-alt transition-colors"
                role="menuitem"
              >
                Get the iPhone app
              </Link>
            )}
            {showSignIn && (
              <>
                <hr className="border-oro-border my-2" />
                <button
                  onClick={handleSignIn}
                  className="block w-full text-left px-4 py-3 text-accent hover:bg-oro-surface-alt transition-colors font-medium"
                  role="menuitem"
                >
                  Sign in
                </button>
              </>
            )}
          </nav>
        </div>,
        document.body
      )}
    </>
  );
}